import type { GameContract, VersionedSave } from '@vpgc/core';
import type { ResourceBag, ResourceId, SpriteId, TechCategory, TechId, ToolRecipeId, ZoneId } from './domain/content.js';
export type { TechCategory, TechId, ToolRecipeId } from './domain/content.js';

export const TEMPTRASH_SCHEMA_VERSION = 4;

export const INPUT_LEFT = 1 << 0;
export const INPUT_RIGHT = 1 << 1;
export const INPUT_UP = 1 << 2;
export const INPUT_DOWN = 1 << 3;
export const INPUT_ACTION = 1 << 4;
export const INPUT_CLOSE_PANEL = 1 << 5;
export const INPUT_OPEN_BUILD_MENU = 1 << 6;
export const INPUT_OPEN_INVENTORY = 1 << 7;
export const INPUT_OPEN_TECH_TREE = 1 << 8;
export const INPUT_OPEN_TOOLS_PANEL = 1 << 9;
export const INPUT_OPEN_DEBUG_PANEL = 1 << 10;

export const TEMPTRASH_NO_INPUT: Input = { mask: 0 };

export type Direction = 'north' | 'east' | 'south' | 'west';
export type TrashKind = ResourceId;
export type InventoryItemKind = 'trash' | 'resource' | 'item';
export type UsableItemId = 'grenade' | 'flamethrower';
export type ToolId = 'hands' | UsableItemId;
export type BuildingId = string;
export type BuildingType = 'recycler' | 'storage' | 'gatherer' | 'carrier_drone' | 'drill';
export type PanelCommand = 'close' | 'depositSelected' | 'depositAll' | 'collectOutput' | 'removeBuilding';
export type BuildCommand = 'open' | 'close' | 'cancel' | BuildingType;
export type UiFocusScope = 'buildMenu' | 'inventory' | 'buildingPanel' | 'techTree' | 'toolsPanel' | 'debugPanel';
export type InventoryLocation = 'player' | 'buildingInput' | 'buildingOutput' | 'buildingStorage';
export type ItemCommand = 'selectHands' | 'selectGrenade' | 'selectFlamethrower' | 'cycleTool' | 'cycleToolBackward';
export type ToolCommand = ToolRecipeId;
export type DebugCommand =
  | 'grantTechPoint'
  | 'grantScrap'
  | 'grantPlastic'
  | 'grantGlass'
  | 'grantBiomass'
  | 'grantAllResources'
  | 'grantGrenade'
  | 'grantFlamethrower';

export interface Input {
  mask: number;
  targetTileX?: number;
  targetTileY?: number;
  selectedSlot?: number;
  inventoryMove?: {
    fromSlot: number;
    toSlot: number;
  };
  transferItem?: {
    from: InventoryLocation;
    slot: number;
  };
  panelItemMove?: {
    from: InventoryLocation;
    fromSlot: number;
    to: InventoryLocation;
    toSlot: number;
  };
  uiFocus?: {
    scope: UiFocusScope;
    index: number;
  };
  actionHeld?: boolean;
  panelCommand?: PanelCommand;
  buildCommand?: BuildCommand;
  techCommand?: TechId;
  itemCommand?: ItemCommand;
  toolCommand?: ToolCommand;
  debugCommand?: DebugCommand;
}

export interface GatherState {
  targetTileX: number;
  targetTileY: number;
  elapsedSec: number;
  durationSec: number;
}

export interface PlayerState {
  x: number;
  y: number;
  facing: Direction;
  speed: number;
  harvestPower: number;
  selectedTool: ToolId;
  toolUseTimerSec: number;
  selectedSlot: number;
  gather: GatherState | null;
}

export interface InventoryItem {
  uid: number;
  kind: InventoryItemKind;
  itemId: 'trash' | ResourceId | UsableItemId;
  resource?: ResourceId;
  quantity: number;
}

export interface InventoryState {
  slots: Array<InventoryItem | null>;
  nextItemUid: number;
}

export interface BuildingProcessState {
  item: InventoryItem;
  elapsedSec: number;
  durationSec: number;
  outputResources: ResourceId[];
}

export interface MobileUnitState {
  id: string;
  type: 'carrier_drone';
  sourceBuildingId: BuildingId;
  x: number;
  y: number;
  mode: 'to_port' | 'to_player' | 'to_recycler';
  cargo: Array<InventoryItem | null>;
  speed: number;
}

export interface BuildingState {
  id: BuildingId;
  type: BuildingType;
  name: string;
  tileX: number;
  tileY: number;
  width: number;
  height: number;
  inbox: Array<InventoryItem | null>;
  outbox: Array<InventoryItem | null>;
  storage: Array<InventoryItem | null>;
  active: BuildingProcessState | null;
  workTimerSec: number;
  facing?: Direction;
  fuel?: number;
}

export interface ZoneProgressState {
  awardedTech: Partial<Record<ZoneId, true>>;
}

export interface TechState {
  points: number;
  levels: Record<TechId, number>;
}

export interface GameState {
  schemaVersion: typeof TEMPTRASH_SCHEMA_VERSION;
  seed: number;
  rng: number;
  tick: number;
  nextBuildingId: number;
  player: PlayerState;
  inventory: InventoryState;
  resources: ResourceBag;
  features: {
    pickupStreak: boolean;
  };
  buildings: BuildingState[];
  mobileUnits: MobileUnitState[];
  tech: TechState;
  zones: ZoneProgressState;
  ui: {
    openPanelBuildingId: BuildingId | null;
    buildMenuOpen: boolean;
    pendingBuildType: BuildingType | null;
    inventoryOpen: boolean;
    techTreeOpen: boolean;
    toolsPanelOpen: boolean;
    debugPanelOpen: boolean;
    buildMenuFocus: number;
    inventoryFocus: number;
    buildingPanelFocus: number;
    techTreeFocus: number;
    toolsPanelFocus: number;
    debugPanelFocus: number;
    statusMessage: string | null;
  };
  world: {
    tileOverrides: Record<string, number>;
    revealedTiles: Record<string, true>;
    gooTiles: Record<string, true>;
    gooSpreadElapsedSec: number;
    gooSpreadStep: number;
  };
  streak: {
    value: number;
    tier: number;
    timeRemainingSec: number;
  };
  fxEvents: ViewFxEvent[];
  stats: {
    tilesCleared: number;
    garbageHarvested: number;
    garbageRecycled: number;
    buildingsBuilt: number;
    techPointsEarned: number;
    trashDestroyed: number;
    gooCleaned: number;
  };
}

export interface ViewTile {
  x: number;
  y: number;
  amount: number;
  maxAmount: number;
  variant: number;
  blocked: boolean;
  wall: boolean;
  goo: boolean;
  gooSource: boolean;
  restored: boolean;
  visible: boolean;
  revealed: boolean;
  spriteId: SpriteId;
  zoneId: ZoneId | null;
  zoneColor: number;
}

export interface ViewFxEvent {
  id: number;
  kind: 'pickup' | 'dropoff' | 'burn' | 'blast';
  resource: ResourceId;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  amount: number;
}

export interface ViewActionTarget {
  tileX: number;
  tileY: number;
  x: number;
  y: number;
  kind: 'harvest' | 'building' | 'place' | 'blocked' | 'empty';
  label: string;
}

export interface ViewBuildOption {
  type: BuildingType;
  name: string;
  description: string;
  footprint: string;
  cost: ResourceBag;
  canBuild: boolean;
}

export interface ViewInventorySlot {
  index: number;
  selected: boolean;
  item: InventoryItem | null;
}

export interface ViewBuilding {
  id: BuildingId;
  type: BuildingType;
  name: string;
  tileX: number;
  tileY: number;
  width: number;
  height: number;
  x: number;
  y: number;
  inboxUsed: number;
  inboxCapacity: number;
  outboxUsed: number;
  outboxCapacity: number;
  storageUsed: number;
  storageCapacity: number;
  active: BuildingProcessState | null;
  status: string;
  progress: number;
  canRemove: boolean;
}

export interface ViewBuildingPanel extends ViewBuilding {
  inbox: Array<InventoryItem | null>;
  outbox: Array<InventoryItem | null>;
  storage: Array<InventoryItem | null>;
  playerInventory: ViewInventorySlot[];
  accepts: string;
  canDepositSelected: boolean;
  canCollectOutput: boolean;
  canRemove: boolean;
  focusedCommand: PanelCommand;
}

export interface ViewBuildMenu {
  open: boolean;
  pendingBuildType: BuildingType | null;
  pendingCost: ResourceBag | null;
  focusedIndex: number;
  options: ViewBuildOption[];
}

export interface ViewInventoryPanel {
  open: boolean;
  selectedSlot: number;
  focusedSlot: number;
  slots: ViewInventorySlot[];
}

export interface ViewTechOption {
  id: TechId;
  name: string;
  description: string;
  category: TechCategory;
  level: number;
  maxLevel: number;
  cost: number;
  canBuy: boolean;
  locked: boolean;
}

export interface ViewToolRecipe {
  id: ToolRecipeId;
  name: string;
  description: string;
  resultItemId: UsableItemId;
  cost: ResourceBag;
  unlocked: boolean;
  canCraft: boolean;
  owned: number;
  unique: boolean;
  status: string;
}

export interface ViewMobileUnit {
  id: string;
  type: MobileUnitState['type'];
  x: number;
  y: number;
  mode: MobileUnitState['mode'];
  cargo: number;
  cargoCapacity: number;
}

export interface ViewGatherProgress {
  active: boolean;
  tileX: number;
  tileY: number;
  progress: number;
}

export interface ViewTechTree {
  open: boolean;
  points: number;
  focusedIndex: number;
  options: ViewTechOption[];
}

export interface ViewToolsPanel {
  open: boolean;
  focusedIndex: number;
  recipes: ViewToolRecipe[];
}

export interface ViewDebugOption {
  command: DebugCommand;
  name: string;
  description: string;
}

export interface ViewDebugPanel {
  open: boolean;
  focusedIndex: number;
  options: ViewDebugOption[];
}

export interface ViewZoneProgress {
  id: ZoneId | null;
  name: string;
  percentCleared: number;
  clearedTrashTiles: number;
  totalTrashTiles: number;
  techAwarded: boolean;
}

export interface ViewModel {
  tick: number;
  camera: {
    x: number;
    y: number;
  };
  player: PlayerState & {
    tileX: number;
    tileY: number;
  };
  actionTarget: ViewActionTarget;
  inventory: {
    slots: ViewInventorySlot[];
    used: number;
    capacity: number;
  };
  home: {
    tileX: number;
    tileY: number;
    x: number;
    y: number;
  };
  resources: ResourceBag;
  resourceOrder: ResourceId[];
  selectedTool: {
    id: ToolId;
    label: string;
    available: boolean;
  };
  buildings: ViewBuilding[];
  mobileUnits: ViewMobileUnit[];
  buildingPanel: ViewBuildingPanel | null;
  buildMenu: ViewBuildMenu;
  inventoryPanel: ViewInventoryPanel;
  techTree: ViewTechTree;
  toolsPanel: ViewToolsPanel;
  debugPanel: ViewDebugPanel;
  tiles: ViewTile[];
  currentZone: ViewZoneProgress;
  gather: ViewGatherProgress | null;
  streak: GameState['streak'] & {
    enabled: boolean;
    nextTierAt: number;
    windowSec: number;
  };
  fxEvents: ViewFxEvent[];
  stats: GameState['stats'];
  statusMessage: string | null;
}

export interface SaveJson extends VersionedSave {
  schemaVersion: typeof TEMPTRASH_SCHEMA_VERSION;
  seed: number;
  rng: number;
  tick: number;
  nextBuildingId: number;
  player: PlayerState;
  inventory: InventoryState;
  resources: ResourceBag;
  features: {
    pickupStreak: boolean;
  };
  buildings: BuildingState[];
  mobileUnits: MobileUnitState[];
  tech: TechState;
  zones: ZoneProgressState;
  ui: GameState['ui'];
  world: {
    tileOverrides: Record<string, number>;
    revealedTiles: Record<string, true>;
    gooTiles: Record<string, true>;
    gooSpreadElapsedSec: number;
    gooSpreadStep: number;
  };
  streak: {
    value: number;
    tier: number;
    timeRemainingSec: number;
  };
  fxEvents: ViewFxEvent[];
  stats: GameState['stats'];
}

export type TemptrashContract = GameContract<GameState, Input, ViewModel, SaveJson>;
